/**
 * Shared hook + helpers voor de "Onderwater venster"-rendering.
 * Berekent per actieve diagram + revealMode de geëmitte PlantUML- /
 * Mermaid- / SVG-tekst plus een unified `currentText` voor de
 * copy-knop / preview-pane.
 *
 * Gebruikt door zowel TextPane (text-modus van het Onderwater venster)
 * als GridPane (tabel-modus, met preview-pane rechts).
 *
 * Geëxtraheerd 2026-05-24 uit TextPane.tsx zodat GridPane dezelfde
 * geëmitte tekst kan tonen + kopiëren zonder code-duplicaat.
 */
import {
  type Concept,
  emitArchiMateMermaid,
  emitArchiMatePlantUML,
  emitC4PlantUML,
  type Relation,
} from "@ae/domain";
import { useMemo } from "react";
import { useUi } from "../../state/ui";
import { useWorkspaceData } from "../../state/workspace";

/**
 * SVG-mode is verwijderd 2026-05-24. Re-export blijft voor componenten
 * die `RevealMode` als type importeren — bron is useUi waar dezelfde
 * union staat.
 */
export type RevealMode = "plantuml" | "mermaid";

interface VisibleSet {
  concepts: Concept[];
  relations: Relation[];
  conceptIdSet: Set<string>;
  relationIdSet: Set<string>;
}

/**
 * Bouwt de set van concepten + relaties die op het actieve diagram
 * zichtbaar zijn. Filter-regels identiek aan Canvas.tsx +
 * GridPane.tsx zodat de geëmitte tekst exact overeenkomt met wat
 * visueel op het canvas staat.
 */
export function useVisibleOnActiveDiagram(): VisibleSet {
  const revision = useWorkspaceData((s) => s.revision);
  const activeDiagramId = useWorkspaceData((s) => s.activeDiagramId);
  return useMemo<VisibleSet>(() => {
    void revision;
    const store = useWorkspaceData.getState().store;
    if (!activeDiagramId) {
      return {
        concepts: [],
        relations: [],
        conceptIdSet: new Set<string>(),
        relationIdSet: new Set<string>(),
      };
    }
    const diagram = store.getDiagram(activeDiagramId);
    if (!diagram) {
      return {
        concepts: [],
        relations: [],
        conceptIdSet: new Set<string>(),
        relationIdSet: new Set<string>(),
      };
    }
    const conceptIdSet = new Set<string>();
    for (const [key, ve] of Object.entries(diagram.viewState)) {
      if (ve.position === undefined) continue;
      if (ve.hidden === true) continue;
      conceptIdSet.add(ve.ref ?? key);
    }
    const concepts = store
      .listConcepts()
      .filter((c) => !c.tombstoneInBranch && conceptIdSet.has(c.id));
    const liveConceptIds = new Set(concepts.map((c) => c.id));
    const placedRelationIds = new Set<string>();
    const allRelations = store.listRelations();
    for (const d of store.listDiagrams()) {
      for (const r of allRelations) {
        if (d.viewState[r.id] !== undefined) placedRelationIds.add(r.id);
      }
    }
    const relations = allRelations.filter((r) => {
      if (r.tombstoneInBranch) return false;
      if (!liveConceptIds.has(r.sourceConceptId)) return false;
      if (!liveConceptIds.has(r.targetConceptId)) return false;
      const ve = diagram.viewState[r.id];
      if (ve?.hidden === true) return false;
      const isLegacy = !placedRelationIds.has(r.id);
      const onThisDiagram = ve !== undefined;
      if (!isLegacy && !onThisDiagram) return false;
      return true;
    });
    const relationIdSet = new Set(relations.map((r) => r.id));
    return { concepts, relations, conceptIdSet, relationIdSet };
  }, [revision, activeDiagramId]);
}

interface RevealEmitters {
  mode: RevealMode;
  plantumlText: string;
  mermaidText: string;
  currentText: string;
  visible: VisibleSet;
}

/**
 * Returnt de drie geëmitte teksten + de currentText afhankelijk van de
 * actieve revealMode. Hergebruikt `useVisibleOnActiveDiagram` voor de
 * filter-set.
 */
export function useRevealEmitters(lang: "nl" | "en"): RevealEmitters {
  const mode = useUi((s) => s.revealMode);
  const lineType = useUi((s) => s.plantumlLineType);
  const perInstance = useUi((s) => s.plantumlPerInstance);
  const revision = useWorkspaceData((s) => s.revision);
  const activeDiagramId = useWorkspaceData((s) => s.activeDiagramId);
  const visible = useVisibleOnActiveDiagram();

  const plantumlText = useMemo(() => {
    void revision;
    const store = useWorkspaceData.getState().store;
    const handles: Record<string, { source?: string; target?: string }> = {};
    const waypoints: Record<string, ReadonlyArray<{ x: number; y: number }>> = {};
    const containment: Record<string, string> = {};
    let title: string | undefined;
    if (activeDiagramId) {
      const diagram = store.getDiagram(activeDiagramId);
      if (diagram) {
        for (const r of visible.relations) {
          const ve = diagram.viewState[r.id];
          if (!ve) continue;
          const entry: { source?: string; target?: string } = {};
          if (ve.sourceHandle) entry.source = ve.sourceHandle;
          if (ve.targetHandle) entry.target = ve.targetHandle;
          if (entry.source || entry.target) handles[r.id] = entry;
          // Canvas-waypoints (bend-points op de lijn) → emitter genereert
          // hidden-rectangle-chain zodat ELK de knikken visueel respecteert.
          if (ve.waypoints && ve.waypoints.length > 0) {
            waypoints[r.id] = ve.waypoints;
          }
        }
        for (const c of visible.concepts) {
          const ve = diagram.viewState[c.id];
          if (!ve?.containerId) continue;
          const parentVe = diagram.viewState[ve.containerId];
          const parentConceptId = parentVe?.ref ?? ve.containerId;
          if (parentConceptId === c.id) continue;
          containment[c.id] = parentConceptId;
        }
        const containerBoxes = visible.concepts
          .filter((g) => !g.typeId.startsWith("drawing."))
          .map((g) => {
            const ve = diagram.viewState[g.id];
            if (!ve?.position) return null;
            const w = ve.width ?? 140;
            const h = ve.height ?? 60;
            return { id: g.id, x: ve.position.x, y: ve.position.y, w, h, area: w * h };
          })
          .filter((b): b is NonNullable<typeof b> => b !== null);
        for (const c of visible.concepts) {
          if (containment[c.id]) continue;
          if (c.typeId.startsWith("drawing.")) continue;
          const ve = diagram.viewState[c.id];
          if (!ve?.position) continue;
          const cw = ve.width ?? 140;
          const ch = ve.height ?? 60;
          const childArea = cw * ch;
          const cx = ve.position.x + cw / 2;
          const cy = ve.position.y + ch / 2;
          let best: { id: string; area: number } | null = null;
          for (const g of containerBoxes) {
            if (g.id === c.id) continue;
            if (g.area < childArea * 1.5) continue;
            if (cx >= g.x && cx <= g.x + g.w && cy >= g.y && cy <= g.y + g.h) {
              if (!best || g.area < best.area) best = { id: g.id, area: g.area };
            }
          }
          if (best) containment[c.id] = best.id;
        }
        const desc = diagram.description
          ? (diagram.description[lang] ?? diagram.description.nl ?? diagram.description.en ?? "")
          : "";
        if (desc.trim().length > 0) title = desc.trim();
      }
    }
    // View-kind-router: c4.* → C4-emitter (Container/Component/Person/
    // System procedures + C4-stdlib include). Anders → ArchiMate-emitter.
    // Sharp-views vallen onder ArchiMate-emit met stereotype-comment-
    // fallback (zie commit C in deze ronde).
    const activeViewKind = activeDiagramId
      ? useWorkspaceData.getState().store.getDiagram(activeDiagramId)?.viewKind
      : undefined;
    if (activeViewKind?.startsWith("c4.")) {
      // C4-emitter gebruikt alleen source-handle (geen target-suffix in
      // stdlib). Strip dus naar `{source}`-only om mee te sturen.
      const c4Handles: Record<string, { source?: string }> = {};
      for (const [relId, h] of Object.entries(handles)) {
        if (h.source) c4Handles[relId] = { source: h.source };
      }
      return emitC4PlantUML(visible.concepts, visible.relations, {
        lang,
        viewKind: activeViewKind,
        lineType,
        ...(Object.keys(c4Handles).length > 0 ? { handles: c4Handles } : {}),
        ...(title ? { title } : {}),
      });
    }
    // Sharp-views (sharp.*) — ArchiMate-stdlib + stereotype-comment-
    // trailer als fallback (geen Sharp-specifieke PlantUML-library
    // beschikbaar). Zie plantuml-aanhechting.md keuze 2.
    const isSharpView = activeViewKind?.startsWith("sharp.") ?? false;
    // Per-instance modus (multi-instance-refactor 2026-05-26):
    // bouw instancesByConcept (lijst entry-keys per concept-id) en
    // relationOccurrences (één item per viewState-relation-entry).
    let instancesByConcept: Record<string, string[]> | undefined;
    let relationOccurrences:
      | Array<{ relationId: string; sourceEntryKey?: string; targetEntryKey?: string }>
      | undefined;
    if (perInstance && activeDiagramId) {
      const diagram = useWorkspaceData.getState().store.getDiagram(activeDiagramId);
      if (diagram) {
        instancesByConcept = {};
        relationOccurrences = [];
        for (const [k, ve] of Object.entries(diagram.viewState)) {
          if (ve.hidden === true) continue;
          if (ve.position !== undefined || ve.type === "object" || ve.slotId !== undefined) {
            const cid = ve.ref ?? k;
            if (!instancesByConcept[cid]) instancesByConcept[cid] = [];
            instancesByConcept[cid].push(k);
            continue;
          }
          // Relation-entry
          if (ve.type === "relation" || (ve.position === undefined && ve.ref !== undefined)) {
            const rid = ve.ref ?? k;
            relationOccurrences.push({
              relationId: rid,
              ...(ve.sourceInstanceId ? { sourceEntryKey: ve.sourceInstanceId } : {}),
              ...(ve.targetInstanceId ? { targetEntryKey: ve.targetInstanceId } : {}),
            });
          }
        }
      }
    }
    return emitArchiMatePlantUML(visible.concepts, visible.relations, {
      lang,
      handles,
      lineType,
      ...(Object.keys(waypoints).length > 0 ? { waypoints } : {}),
      ...(Object.keys(containment).length > 0 ? { containment } : {}),
      ...(title ? { title } : {}),
      ...(isSharpView ? { includeStereotypeComments: true } : {}),
      ...(instancesByConcept ? { instancesByConcept } : {}),
      ...(relationOccurrences ? { relationOccurrences } : {}),
    });
  }, [revision, lang, activeDiagramId, visible, lineType, perInstance]);

  const mermaidText = useMemo(() => {
    if (mode !== "mermaid") return "";
    void revision;
    return emitArchiMateMermaid(visible.concepts, visible.relations, { lang });
  }, [mode, revision, lang, visible]);

  const currentText = mode === "plantuml" ? plantumlText : mermaidText;

  return { mode, plantumlText, mermaidText, currentText, visible };
}
